
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { 
  Users, Settings, Plus, Trash2, RefreshCw, Download, Loader2, Search, Edit, UserCheck, X, FileSpreadsheet, Clock, LogOut, PieChart, Briefcase, CalendarDays, Printer, Save, Building, Plane, Wrench, Database, Lock, ExternalLink, Bell, Check, X as XIcon, ChevronLeft, ChevronRight, Filter, Image as ImageIcon, Calendar, Cloud, Globe, ShieldCheck, Coffee, Banknote, RotateCcw, EyeOff
} from 'lucide-react';
import { User, AttendanceLog, AppConfig, Role, LogType, LeaveRecord, LeaveType, CloudBackup, Notification, NotificationStatus, SalaryRecord } from '../types';
import * as storage from '../services/storage';

interface AdminDashboardProps {
  currentUser: User;
  onLogout: () => void;
}

// --- HELPER FUNCTIONS ---
const timeStringToMinutes = (timeStr: string): number => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return (hours * 60) + minutes;
};

const minutesToDuration = (minutes: number): string => {
    if (!minutes || minutes === 0) return "-";
    const absMin = Math.abs(minutes);
    const h = Math.floor(absMin / 60);
    const m = absMin % 60;
    const timeStr = h > 0 ? `${h} sa ${m} dk` : `${m} dk`;
    return minutes < 0 ? `-${timeStr}` : timeStr;
};

const getMinutesFromTimestamp = (timestamp: number): number => {
    try {
        const date = new Date(timestamp);
        const timeStr = date.toLocaleTimeString('en-US', { 
            timeZone: 'Europe/Istanbul', 
            hour12: false, 
            hour: '2-digit', 
            minute: '2-digit' 
        });
        if (timeStr === '24:00') return 0;
        const [h, m] = timeStr.split(':').map(Number);
        return (h * 60) + m;
    } catch (e) {
        const date = new Date(timestamp);
        return (date.getHours() * 60) + date.getMinutes();
    }
};

const formatTimeTR = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Istanbul'
    });
};

const normalizeTR = (text: string) => {
    if(!text) return "";
    const trMap: {[key: string]: string} = {
        'ç': 'c', 'Ç': 'C',
        'ğ': 'g', 'Ğ': 'G',
        'ş': 's', 'Ş': 'S',
        'ü': 'u', 'Ü': 'U',
        'ı': 'i', 'İ': 'I',
        'ö': 'o', 'Ö': 'O'
    };
    return text.replace(/[çÇğĞşŞüÜıİöÖ]/g, (char) => trMap[char] || char);
};

const parseSalary = (salaryStr?: string | number): number => {
    if (salaryStr === undefined || salaryStr === null || salaryStr === '') return 0;
    let cleanStr = String(salaryStr); 
    cleanStr = cleanStr.replace(/\./g, '');
    cleanStr = cleanStr.replace(',', '.');
    cleanStr = cleanStr.replace(/[^0-9.]/g, '');
    return parseFloat(cleanStr) || 0;
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
};

const MONTHS = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

const DAYS_TR = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

// --- OPTIMIZATION HELPER: Group Logs By Date ---
const groupLogsByDate = (logs: AttendanceLog[]) => {
    const map = new Map<string, AttendanceLog[]>();
    logs.forEach(log => {
        const dateKey = new Date(log.timestamp).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
        if (!map.has(dateKey)) {
            map.set(dateKey, []);
        }
        map.get(dateKey)?.push(log);
    });
    map.forEach(group => group.sort((a, b) => a.timestamp - b.timestamp));
    return map;
};

// --- MODALS ---

const UserReportModal: React.FC<{ 
    isOpen: boolean; 
    onClose: () => void; 
    user: User | null; 
    logs: AttendanceLog[];
    leaves: LeaveRecord[];
    config: AppConfig | null;
    initialMonth?: string; // "YYYY-MM" to open specific month
    onUpdateLog: (userId: string, dateStr: string, type: LogType, timeStr: string) => Promise<void>;
}> = ({ isOpen, onClose, user, logs, leaves, config, initialMonth, onUpdateLog }) => {
    const [month, setMonth] = useState(new Date().getMonth());
    const [year, setYear] = useState(new Date().getFullYear());
    const [editingCell, setEditingCell] = useState<{date: string, type: 'entry'|'exit'} | null>(null);
    const [editValue, setEditValue] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if(isOpen) {
            if (initialMonth) {
                const [y, m] = initialMonth.split('-').map(Number);
                setYear(y);
                setMonth(m - 1); // 0-indexed month
            } else {
                setMonth(new Date().getMonth());
                setYear(new Date().getFullYear());
            }
            setEditingCell(null);
        }
    }, [isOpen, initialMonth]);

    const groupedLogs = useMemo(() => groupLogsByDate(logs), [logs]);

    if (!isOpen || !user) return null;

    const generateMonthData = () => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const rows = [];
        let stats = {
            totalWorkDays: 0,
            totalMissingMin: 0,
            totalOvertimeMin: 0,
            totalHolidayOvertimeMin: 0,
            totalLateMin: 0,
            absentDays: 0,
            leaveDays: 0
        };

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dateObj = new Date(year, month, day);
            const dayOfWeek = dateObj.getDay();

            const dailyLogs = groupedLogs.get(dateStr) || [];

            const firstEntry = dailyLogs.find(l => l.type === LogType.ENTRY);
            const lastExit = [...dailyLogs].reverse().find(l => l.type === LogType.EXIT);

            let breakMin = 0;
            for(let i = 0; i < dailyLogs.length - 1; i++) {
                if (dailyLogs[i].type === LogType.EXIT && dailyLogs[i+1].type === LogType.ENTRY) {
                    const exitTime = getMinutesFromTimestamp(dailyLogs[i].timestamp);
                    const nextEntryTime = getMinutesFromTimestamp(dailyLogs[i+1].timestamp);
                    if (nextEntryTime > exitTime) {
                         breakMin += (nextEntryTime - exitTime);
                    }
                }
            }

            const isHoliday = config?.publicHolidays?.includes(dateStr);
            const isSunday = dayOfWeek === 0;
            const isSaturday = dayOfWeek === 6;
            const isSatWorkDay = user.shiftConfig?.isSaturdayWorkDay ?? config?.isSaturdayWorkDay ?? false;
            const isFullOvertimeDay = isHoliday || isSunday || (isSaturday && !isSatWorkDay);

            const sStart = user.shiftConfig?.start || config?.shiftStart || "08:00";
            const sEnd = user.shiftConfig?.end || config?.shiftEnd || "18:00";
            const satStart = user.shiftConfig?.saturdayStart || config?.saturdayStart || "09:00";
            const satEnd = user.shiftConfig?.saturdayEnd || config?.saturdayEnd || "14:00";

            const targetStartMin = (isSaturday && isSatWorkDay) ? timeStringToMinutes(satStart) : timeStringToMinutes(sStart);
            const targetEndMin = (isSaturday && isSatWorkDay) ? timeStringToMinutes(satEnd) : timeStringToMinutes(sEnd);

            let lateMin = 0, earlyMin = 0, missingMin = 0, overtimeMin = 0, holidayOvertimeMin = 0;
            let status = '';
            let statusColor = 'text-slate-400';

            const activeLeave = leaves.find(l => l.startDate <= dateStr && l.endDate >= dateStr);

            if (activeLeave) {
                status = activeLeave.type;
                statusColor = 'text-purple-400 font-bold';
                stats.leaveDays++;
            } else if (firstEntry && lastExit) {
                stats.totalWorkDays++;
                const entryMin = getMinutesFromTimestamp(firstEntry.timestamp);
                const exitMin = getMinutesFromTimestamp(lastExit.timestamp);
                const duration = exitMin - entryMin;

                if (isFullOvertimeDay) {
                    if (isHoliday) {
                        holidayOvertimeMin = duration - breakMin;
                        status = 'BAYRAM MESAİ';
                        statusColor = 'text-purple-500 font-bold';
                    } else if (isSunday) {
                        overtimeMin = duration - breakMin;
                        status = 'PAZAR MESAİ';
                        statusColor = 'text-green-500 font-bold';
                    } else {
                        overtimeMin = duration - breakMin;
                        status = 'CUMARTESİ MESAİ';
                        statusColor = 'text-green-500 font-bold';
                    }
                } else {
                    status = 'GELDİ';
                    statusColor = 'text-white';
                    
                    if (entryMin > targetStartMin) lateMin = entryMin - targetStartMin;
                    if (exitMin < targetEndMin) earlyMin = targetEndMin - exitMin;
                    
                    missingMin = lateMin + earlyMin + breakMin;
                    
                    if (exitMin > targetEndMin) {
                        overtimeMin = exitMin - targetEndMin;
                    }
                }
            } else if (firstEntry || lastExit) {
                status = 'EKSİK LOG';
                statusColor = 'text-orange-500 font-bold';
            } else {
                 if (isHoliday) { status = 'RESMİ TATİL'; statusColor = 'text-slate-500'; }
                 else if (isSunday) { status = 'PAZAR TATİL'; statusColor = 'text-slate-500'; }
                 else if (isSaturday && !isSatWorkDay) { status = 'HAFTA SONU'; statusColor = 'text-slate-500'; }
                 else if (dateObj < new Date()) { status = 'GELMEDİ'; statusColor = 'text-red-500 font-bold'; stats.absentDays++; }
                 else { status = '-'; }
            }

            stats.totalLateMin += lateMin;
            stats.totalMissingMin += missingMin;
            stats.totalOvertimeMin += overtimeMin;
            stats.totalHolidayOvertimeMin += holidayOvertimeMin;

            rows.push({
                dateStr,
                dayName: DAYS_TR[dayOfWeek],
                firstEntry,
                lastExit,
                lateMin,
                earlyMin,
                breakMin,
                missingMin,
                overtimeMin,
                holidayOvertimeMin,
                status,
                statusColor,
                isFullOvertimeDay
            });
        }
        return { rows, stats };
    };

    const { rows, stats } = generateMonthData();

    const handleCellClick = (dateStr: string, type: 'entry'|'exit', currentVal: string) => {
        setEditingCell({ date: dateStr, type });
        setEditValue(currentVal === '-' ? '' : currentVal);
    };

    const handleSave = async (dateStr: string, type: 'entry'|'exit') => {
        if (!editValue) return;
        setIsSaving(true);
        const logType = type === 'entry' ? LogType.ENTRY : LogType.EXIT;
        await onUpdateLog(user.id, dateStr, logType, editValue);
        setEditingCell(null);
        setIsSaving(false);
    };

    const downloadAsImage = async () => {
        const originalElement = document.getElementById('report-modal-content');
        if (originalElement) {
            setIsDownloading(true);
            try {
                const clone = originalElement.cloneNode(true) as HTMLElement;
                clone.style.position = 'fixed';
                clone.style.top = '-9999px';
                clone.style.left = '0';
                clone.style.width = '1200px';
                clone.style.height = 'auto';
                clone.style.overflow = 'visible';
                clone.style.zIndex = '-1000';
                
                const tableContainer = clone.querySelector('.overflow-auto');
                if (tableContainer) {
                    (tableContainer as HTMLElement).style.overflow = 'visible';
                    (tableContainer as HTMLElement).style.height = 'auto';
                }

                document.body.appendChild(clone);
                const canvas = await html2canvas(clone, { 
                    backgroundColor: '#0f172a',
                    scale: 2,
                    windowWidth: 1200
                });
                document.body.removeChild(clone);

                const link = document.createElement('a');
                link.download = `Rapor_${user.fullName.replace(/\s+/g, '_')}_${MONTHS[month]}_${year}.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
            } catch (error) {
                console.error("Image download error:", error);
                alert("Rapor indirilirken bir hata oluştu.");
            } finally {
                setIsDownloading(false);
            }
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm p-4 overflow-hidden">
            <div className="bg-slate-900 rounded-2xl w-full max-w-6xl h-[95vh] flex flex-col shadow-2xl border border-slate-700 animate-in fade-in zoom-in duration-200">
                <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-950">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-blue-900/50">{user.fullName.charAt(0)}</div>
                        <div>
                            <h2 className="font-bold text-xl text-white">{user.fullName}</h2>
                            <div className="flex items-center gap-2 text-sm text-slate-400"><Building className="w-3 h-3"/> {user.department}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex bg-slate-800 rounded-lg p-1 border border-slate-700 items-center">
                             <select value={month} onChange={e => setMonth(Number(e.target.value))} className="bg-slate-800 text-white text-sm font-bold px-3 py-1.5 outline-none cursor-pointer rounded-md border-r border-slate-700 hover:bg-slate-700 transition-colors">
                                {MONTHS.map((m, i) => <option key={i} value={i} className="bg-slate-800 text-white">{m}</option>)}
                             </select>
                             <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} className="bg-slate-800 text-white text-sm font-bold px-3 py-1.5 outline-none w-20 text-center rounded-md hover:bg-slate-700 transition-colors appearance-none"/>
                        </div>
                        <button onClick={downloadAsImage} disabled={isDownloading} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold text-sm transition-colors shadow-lg shadow-blue-900/30">
                            {isDownloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <ImageIcon className="w-4 h-4" />} Resim Olarak İndir
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"><X className="w-6 h-6" /></button>
                    </div>
                </div>

                <div id="report-modal-content" className="flex flex-col flex-1 overflow-hidden bg-slate-950">
                    <div className="p-4 bg-slate-900 border-b border-slate-800 flex justify-between items-center">
                        <div className="text-sm text-slate-400 font-bold uppercase tracking-widest">{MONTHS[month]} {year} Raporu</div>
                        <div className="text-xs text-slate-500">Oluşturulma: {new Date().toLocaleDateString('tr-TR')}</div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-slate-900 border-b border-slate-800">
                        <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50">
                            <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Çalışılan Gün</div>
                            <div className="text-2xl font-black text-white">{stats.totalWorkDays}</div>
                        </div>
                        <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50">
                            <div className="text-[10px] font-bold text-red-400 uppercase mb-1">Toplam Eksik</div>
                            <div className="text-2xl font-black text-red-500">{minutesToDuration(stats.totalMissingMin)}</div>
                        </div>
                        <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50">
                            <div className="text-[10px] font-bold text-green-400 uppercase mb-1">Toplam Fazla Mesai</div>
                            <div className="text-2xl font-black text-green-500">{minutesToDuration(stats.totalOvertimeMin)}</div>
                        </div>
                        <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50">
                            <div className="text-[10px] font-bold text-purple-400 uppercase mb-1">Toplam Bayram Mesai</div>
                            <div className="text-2xl font-black text-purple-500">{minutesToDuration(stats.totalHolidayOvertimeMin)}</div>
                        </div>
                        <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700/50">
                             <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">Devamsızlık / İzin</div>
                             <div className="flex gap-2 text-sm font-bold">
                                <span className="text-red-400">{stats.absentDays} Gelmedi</span>
                                <span className="text-slate-600">|</span>
                                <span className="text-blue-400">{stats.leaveDays} İzin</span>
                             </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto bg-slate-950 p-0 relative">
                        <table className="w-full text-sm text-left border-collapse">
                            <thead className="bg-slate-900 text-xs uppercase font-bold text-slate-300 sticky top-0 z-10 shadow-md">
                                <tr>
                                    <th className="p-4 border-b border-slate-800 w-32">Tarih</th>
                                    <th className="p-4 border-b border-slate-800 w-24">Gün</th>
                                    <th className="p-4 border-b border-slate-800 w-28 bg-slate-900/90 backdrop-blur">Giriş</th>
                                    <th className="p-4 border-b border-slate-800 w-28 bg-slate-900/90 backdrop-blur">Çıkış</th>
                                    <th className="p-4 border-b border-slate-800 w-28 text-center text-orange-400">Ara (Dışarı)</th>
                                    <th className="p-4 border-b border-slate-800 text-center text-red-400">Eksik</th>
                                    <th className="p-4 border-b border-slate-800 text-center text-green-400">F. Mesai</th>
                                    <th className="p-4 border-b border-slate-800 text-center text-purple-400">B. Mesai</th>
                                    <th className="p-4 border-b border-slate-800 text-right">Durum</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/50">
                                {rows.map((row, idx) => {
                                    const entryTime = row.firstEntry ? formatTimeTR(row.firstEntry.timestamp) : '-';
                                    const exitTime = row.lastExit ? formatTimeTR(row.lastExit.timestamp) : '-';
                                    const isEditingEntry = editingCell?.date === row.dateStr && editingCell?.type === 'entry';
                                    const isEditingExit = editingCell?.date === row.dateStr && editingCell?.type === 'exit';

                                    return (
                                    <tr key={idx} className="hover:bg-slate-900/40 transition-colors group">
                                        <td className="p-4 font-mono text-slate-300 font-medium">{new Date(row.dateStr).toLocaleDateString('tr-TR')}</td>
                                        <td className="p-4 text-slate-500 font-medium">{row.dayName}</td>
                                        <td className="p-2 relative">
                                            {isEditingEntry ? (
                                                <div className="flex items-center gap-1 absolute inset-0 px-2 bg-slate-800 z-10">
                                                    <input type="time" autoFocus className="bg-slate-950 border border-slate-600 rounded text-white text-sm p-1 w-full" value={editValue} onChange={e => setEditValue(e.target.value)} />
                                                    <button onClick={() => handleSave(row.dateStr, 'entry')} className="bg-green-600 hover:bg-green-500 text-white p-1.5 rounded"><Save className="w-3 h-3"/></button>
                                                    <button onClick={() => setEditingCell(null)} className="text-red-400 hover:text-red-300 p-1.5"><X className="w-3 h-3"/></button>
                                                </div>
                                            ) : (
                                                <div onClick={() => handleCellClick(row.dateStr, 'entry', entryTime)} className="cursor-pointer group/cell flex items-center justify-between p-2 rounded hover:bg-slate-800 border border-transparent hover:border-slate-700 transition-all">
                                                    <span className={`font-mono font-bold ${entryTime === '-' ? 'text-slate-600' : 'text-white'}`}>{entryTime}</span>
                                                    <Edit className="w-3 h-3 text-slate-600 opacity-0 group-hover/cell:opacity-100 transition-opacity" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-2 relative">
                                            {isEditingExit ? (
                                                <div className="flex items-center gap-1 absolute inset-0 px-2 bg-slate-800 z-10">
                                                    <input type="time" autoFocus className="bg-slate-950 border border-slate-600 rounded text-white text-sm p-1 w-full" value={editValue} onChange={e => setEditValue(e.target.value)} />
                                                    <button onClick={() => handleSave(row.dateStr, 'exit')} className="bg-green-600 hover:bg-green-500 text-white p-1.5 rounded"><Save className="w-3 h-3"/></button>
                                                    <button onClick={() => setEditingCell(null)} className="text-red-400 hover:text-red-300 p-1.5"><X className="w-3 h-3"/></button>
                                                </div>
                                            ) : (
                                                <div onClick={() => handleCellClick(row.dateStr, 'exit', exitTime)} className="cursor-pointer group/cell flex items-center justify-between p-2 rounded hover:bg-slate-800 border border-transparent hover:border-slate-700 transition-all">
                                                    <span className={`font-mono font-bold ${exitTime === '-' ? 'text-slate-600' : 'text-white'}`}>{exitTime}</span>
                                                    <Edit className="w-3 h-3 text-slate-600 opacity-0 group-hover/cell:opacity-100 transition-opacity" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 text-center">{row.breakMin > 0 ? <span className="bg-orange-500/10 text-orange-400 border border-orange-500/20 px-2 py-1 rounded text-xs font-bold">{minutesToDuration(row.breakMin)}</span> : '-'}</td>
                                        <td className="p-4 text-center">{row.missingMin > 0 ? <span className="bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-1 rounded text-xs font-bold">{minutesToDuration(row.missingMin)}</span> : '-'}</td>
                                        <td className="p-4 text-center">{row.overtimeMin > 0 ? <span className="text-green-400 font-bold">+{minutesToDuration(row.overtimeMin)}</span> : '-'}</td>
                                        <td className="p-4 text-center">{row.holidayOvertimeMin > 0 ? <span className="text-purple-400 font-bold">+{minutesToDuration(row.holidayOvertimeMin)}</span> : '-'}</td>
                                        <td className={`p-4 text-right font-medium ${row.statusColor}`}>{row.status}</td>
                                    </tr>
                                )})}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ... UserModal, DeleteConfirmModal, UserLeavesModal are omitted for brevity as they are unchanged ...
const UserModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSave: (user: User) => void;
    user: User | null;
    departments: string[];
}> = ({ isOpen, onClose, onSave, user, departments }) => {
    const [formData, setFormData] = useState<Partial<User>>({
        username: '', password: '', fullName: '', role: Role.USER, department: '', isActive: true, employmentStartDate: new Date().toISOString().split('T')[0], salary: '',
        shiftConfig: { start: '08:00', end: '18:00', isSaturdayWorkDay: false, saturdayStart: '09:00', saturdayEnd: '14:00' },
        isFixedSalary: false,
        isExcludedFromSalary: false
    });
    useEffect(() => {
        if (user) {
            setFormData({
                ...user,
                shiftConfig: user.shiftConfig || { start: '08:00', end: '18:00', isSaturdayWorkDay: false, saturdayStart: '09:00', saturdayEnd: '14:00' },
                isFixedSalary: user.isFixedSalary || false,
                isExcludedFromSalary: user.isExcludedFromSalary || false
            });
        } else {
            setFormData({ 
                username: '', password: '', fullName: '', role: Role.USER, department: departments[0] || '', isActive: true, employmentStartDate: new Date().toISOString().split('T')[0], salary: '',
                shiftConfig: { start: '08:00', end: '18:00', isSaturdayWorkDay: false, saturdayStart: '09:00', saturdayEnd: '14:00' },
                isFixedSalary: false,
                isExcludedFromSalary: false
            });
        }
    }, [user, isOpen, departments]);

    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm p-4">
            <div className="bg-slate-900 rounded-2xl w-full max-w-2xl p-6 shadow-2xl border border-slate-700 h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6 border-b border-slate-800 pb-4">
                    <h2 className="text-xl font-bold text-white">{user ? 'Personeli Düzenle' : 'Yeni Personel Ekle'}</h2>
                    <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-white"/></button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); onSave(formData as User); }} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="text-xs font-bold text-slate-400 mb-1 block">Ad Soyad</label><input required className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white" value={formData.fullName} onChange={e => setFormData({ ...formData, fullName: e.target.value })} /></div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 mb-1 block">Departman</label>
                            <select className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white" value={formData.department} onChange={e => setFormData({ ...formData, department: e.target.value })}>
                                <option value="">Seçiniz</option>
                                {departments.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="text-xs font-bold text-slate-400 mb-1 block">Kullanıcı Adı</label><input required className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white" value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value })} /></div>
                        <div><label className="text-xs font-bold text-slate-400 mb-1 block">Şifre</label><input required className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-slate-400 mb-1 block">Rol</label>
                            <select className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white" value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value as Role })}>
                                <option value={Role.USER}>Personel</option>
                                <option value={Role.ADMIN}>Yönetici</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 mb-1 block">Maaş (TL)</label>
                            <input type="text" className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white" value={formData.salary} onChange={e => setFormData({ ...formData, salary: e.target.value })} placeholder="örn. 29.500" />
                        </div>
                    </div>
                    <div>
                         <label className="text-xs font-bold text-slate-400 mb-1 block">İşe Başlama</label>
                         <input type="date" className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white" value={formData.employmentStartDate} onChange={e => setFormData({ ...formData, employmentStartDate: e.target.value })} />
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <input type="checkbox" id="isFixedSalary" checked={formData.isFixedSalary || false} onChange={e => setFormData({ ...formData, isFixedSalary: e.target.checked })} className="w-5 h-5 text-blue-600 bg-slate-700 border-slate-600 rounded" />
                        <label htmlFor="isFixedSalary" className="text-sm font-medium text-slate-300">Sabit Maaş (Mesai/Kesinti Hesaplanmaz)</label>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-red-900/10 rounded-lg border border-red-900/30">
                        <input type="checkbox" id="isExcluded" checked={formData.isExcludedFromSalary || false} onChange={e => setFormData({ ...formData, isExcludedFromSalary: e.target.checked })} className="w-5 h-5 text-red-600 bg-slate-700 border-slate-600 rounded" />
                        <label htmlFor="isExcluded" className="text-sm font-medium text-red-200">Maaş Listesinden Gizle (Listeye Dahil Etme)</label>
                    </div>

                    <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 mt-4">
                        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-blue-500"/> Çalışma Saatleri ve Vardiya</h3>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                            <div><label className="text-xs font-bold text-slate-500 mb-1 block">Hafta İçi Başlangıç</label><input type="time" className="w-full p-2 bg-slate-900 border border-slate-700 rounded-lg text-white" value={formData.shiftConfig?.start} onChange={e => setFormData({...formData, shiftConfig: {...formData.shiftConfig!, start: e.target.value}})} /></div>
                            <div><label className="text-xs font-bold text-slate-500 mb-1 block">Hafta İçi Bitiş</label><input type="time" className="w-full p-2 bg-slate-900 border border-slate-700 rounded-lg text-white" value={formData.shiftConfig?.end} onChange={e => setFormData({...formData, shiftConfig: {...formData.shiftConfig!, end: e.target.value}})} /></div>
                        </div>
                        <div className="pt-3 border-t border-slate-800">
                             <div className="flex items-center gap-3 mb-3">
                                <input type="checkbox" id="satWork" checked={formData.shiftConfig?.isSaturdayWorkDay} onChange={e => setFormData({...formData, shiftConfig: {...formData.shiftConfig!, isSaturdayWorkDay: e.target.checked}})} className="w-4 h-4 text-blue-600 bg-slate-900 border-slate-700 rounded focus:ring-blue-500" />
                                <label htmlFor="satWork" className="text-sm font-bold text-slate-300">Cumartesi Normal Çalışma Günü</label>
                             </div>
                             {formData.shiftConfig?.isSaturdayWorkDay ? (
                                 <div className="grid grid-cols-2 gap-4">
                                    <div><label className="text-xs font-bold text-blue-400 mb-1 block">Cmt Başlangıç</label><input type="time" className="w-full p-2 bg-slate-900 border border-slate-700 rounded-lg text-white" value={formData.shiftConfig?.saturdayStart || "09:00"} onChange={e => setFormData({...formData, shiftConfig: {...formData.shiftConfig!, saturdayStart: e.target.value}})} /></div>
                                    <div><label className="text-xs font-bold text-blue-400 mb-1 block">Cmt Bitiş</label><input type="time" className="w-full p-2 bg-slate-900 border border-slate-700 rounded-lg text-white" value={formData.shiftConfig?.saturdayEnd || "14:00"} onChange={e => setFormData({...formData, shiftConfig: {...formData.shiftConfig!, saturdayEnd: e.target.value}})} /></div>
                                 </div>
                             ) : <p className="text-xs text-orange-400 font-medium ml-7">İşaretli değilse Cumartesi fazla mesai sayılır.</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <input type="checkbox" id="isActive" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} className="w-5 h-5 text-blue-600 bg-slate-700 border-slate-600 rounded" />
                        <label htmlFor="isActive" className="text-sm font-medium text-slate-300">Bu personel hesabı aktif</label>
                    </div>
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-800">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg">İptal</button>
                        <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700">Kaydet</button>
                    </div>
                </form>
            </div>
        </div>
    );
};
const DeleteConfirmModal: React.FC<{ isOpen: boolean; onClose: () => void; onConfirm: () => void; title: string; message: string; }> = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm p-4">
            <div className="bg-slate-900 rounded-2xl w-full max-w-sm p-6 shadow-2xl border border-slate-800 text-center">
                <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20"><UserCheck className="w-8 h-8 text-red-500" /></div>
                <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
                <p className="text-sm text-slate-400 mb-6 leading-relaxed">{message}</p>
                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 py-2.5 bg-slate-800 text-slate-300 rounded-xl font-bold">İptal</button>
                    <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700">Sil</button>
                </div>
            </div>
        </div>
    );
};
const UserLeavesModal: React.FC<{ isOpen: boolean; onClose: () => void; user: User | null; }> = ({ isOpen, onClose, user }) => {
    const [leaves, setLeaves] = useState<LeaveRecord[]>([]);
    const [formData, setFormData] = useState({
        startDate: '',
        endDate: '',
        type: LeaveType.ANNUAL,
        description: ''
    });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => { 
        if (isOpen && user) {
            loadLeaves();
        }
    }, [isOpen, user]);

    const loadLeaves = async () => {
        if (!user) return;
        const data = await storage.getLeavesByUser(user.id);
        setLeaves(data.sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()));
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !formData.startDate || !formData.endDate) return;

        setIsLoading(true);
        try {
            const start = new Date(formData.startDate); 
            const end = new Date(formData.endDate);
            const diffTime = Math.abs(end.getTime() - start.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; 

            await storage.addLeave({ 
                userId: user.id, 
                userFullName: user.fullName, 
                startDate: formData.startDate, 
                endDate: formData.endDate, 
                type: formData.type, 
                daysDeducted: diffDays, 
                description: formData.description 
            });
            
            await loadLeaves();
            setFormData({ startDate: '', endDate: '', type: LeaveType.ANNUAL, description: '' });
        } catch (error) {
            console.error(error);
            alert("İzin eklenirken hata oluştu.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id: string) => { 
        if(!confirm("Bu izin kaydını silmek istediğinize emin misiniz?")) return;
        await storage.deleteLeave(id); 
        await loadLeaves(); 
    };

    if (!isOpen || !user) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm p-4">
            <div className="bg-slate-900 rounded-2xl w-full max-w-5xl h-[85vh] flex flex-col shadow-2xl border border-slate-700">
                <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-950 rounded-t-2xl">
                    <div>
                        <h3 className="font-bold text-xl text-white flex items-center gap-3">
                            <div className="p-2 bg-purple-500/10 rounded-lg text-purple-400"><Plane className="w-6 h-6"/></div>
                            İzin Yönetimi
                        </h3>
                        <p className="text-slate-400 text-sm mt-1 ml-1">{user.fullName} ({user.department})</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"><X className="w-6 h-6" /></button>
                </div>
                
                <div className="flex flex-col lg:flex-row h-full overflow-hidden">
                    {/* Form Section */}
                    <div className="w-full lg:w-1/3 p-6 bg-slate-900 border-r border-slate-800 overflow-y-auto">
                        <h4 className="text-white font-bold mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-blue-500"/> Yeni İzin Ekle</h4>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">İzin Türü</label>
                                <select className="w-full p-3 bg-slate-950 border border-slate-700 rounded-xl text-white outline-none focus:ring-2 focus:ring-purple-500" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value as LeaveType})}>
                                    {Object.values(LeaveType).map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Başlangıç</label>
                                    <input type="date" required className="w-full p-3 bg-slate-950 border border-slate-700 rounded-xl text-white outline-none focus:ring-2 focus:ring-purple-500 text-sm" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Bitiş</label>
                                    <input type="date" required className="w-full p-3 bg-slate-950 border border-slate-700 rounded-xl text-white outline-none focus:ring-2 focus:ring-purple-500 text-sm" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Açıklama (Opsiyonel)</label>
                                <textarea className="w-full p-3 bg-slate-950 border border-slate-700 rounded-xl text-white outline-none focus:ring-2 focus:ring-purple-500 h-24 resize-none text-sm" placeholder="İzin nedeni..." value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
                            </div>
                            <button type="submit" disabled={isLoading} className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-900/20 disabled:opacity-50">
                                {isLoading ? <Loader2 className="w-5 h-5 animate-spin"/> : 'Kaydet ve Ekle'}
                            </button>
                        </form>
                    </div>

                    {/* List Section */}
                    <div className="w-full lg:w-2/3 flex flex-col bg-slate-950">
                         <div className="p-4 border-b border-slate-800 bg-slate-900/50">
                             <h4 className="text-slate-300 font-bold text-sm">İzin Geçmişi</h4>
                         </div>
                         <div className="flex-1 overflow-auto p-4">
                            {leaves.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                                    <Plane className="w-12 h-12 mb-2 opacity-20"/>
                                    <p>Henüz izin kaydı bulunmamaktadır.</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {leaves.map(l => (
                                        <div key={l.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:border-slate-700 transition-colors group">
                                            <div>
                                                <div className="flex items-center gap-3 mb-1">
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${l.type === LeaveType.ANNUAL ? 'bg-blue-500/20 text-blue-400' : l.type === LeaveType.SICK ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-300'}`}>{l.type}</span>
                                                    <span className="text-slate-500 text-xs font-mono">{new Date(l.startDate).toLocaleDateString('tr-TR')} - {new Date(l.endDate).toLocaleDateString('tr-TR')}</span>
                                                </div>
                                                <div className="text-white font-bold text-sm">{l.daysDeducted} Gün</div>
                                                {l.description && <div className="text-slate-500 text-xs mt-1 italic">"{l.description}"</div>}
                                            </div>
                                            <button onClick={() => handleDelete(l.id)} className="p-2 text-slate-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors" title="Sil">
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                         </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ currentUser, onLogout }) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'reports' | 'users' | 'settings' | 'notifications' | 'salary'>('dashboard');
  const [settingsTab, setSettingsTab] = useState<'company' | 'departments' | 'security' | 'data'>('company');
  
  const [users, setUsers] = useState<User[]>([]);
  const [logs, setLogs] = useState<AttendanceLog[]>([]);
  const [leaves, setLeaves] = useState<LeaveRecord[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  
  // FIX: Use 'ALL' OR NotificationStatus Enum type
  const [notifFilterStatus, setNotifFilterStatus] = useState<'ALL' | NotificationStatus>(NotificationStatus.PENDING);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [reportModalUser, setReportModalUser] = useState<User | null>(null);
  const [leavesModalUser, setLeavesModalUser] = useState<User | null>(null);
  const [deleteModalId, setDeleteModalId] = useState<string | null>(null);

  const [reportFilter, setReportFilter] = useState({ startDate: new Date().toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0], department: '' });
  const [userSearch, setUserSearch] = useState('');
  const [userFilterDept, setUserFilterDept] = useState('');
  
  // NEW: Report State
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM

  const [configForm, setConfigForm] = useState<Partial<AppConfig>>({});
  const [newHoliday, setNewHoliday] = useState('');
  const [newDeptName, setNewDeptName] = useState('');
  const [kioskPassword, setKioskPassword] = useState('');
  const [adminCreds, setAdminCreds] = useState({ username: '', password: '' });

  const [isRepairing, setIsRepairing] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [cloudBackups, setCloudBackups] = useState<CloudBackup[]>([]);
  const [isCloudLoading, setIsCloudLoading] = useState(false);
  const [cloudStatusMessage, setCloudStatusMessage] = useState<string>('');

  const [serverTime, setServerTime] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- SALARY STATE ---
  const [salaryMonth, setSalaryMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [salaryRecords, setSalaryRecords] = useState<SalaryRecord[]>([]);

  // SORTED USERS MEMO
  const sortedUsers = useMemo(() => {
      return [...users].sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'));
  }, [users]);
  
  // MONTHLY STATS SUMMARY FOR REPORTS TAB
  // OPTIMIZED: Use Map lookup instead of Array.filter inside loops
  const monthlyStats = useMemo(() => {
      if (!config || !users.length) return [];
      
      const [yearStr, monthStr] = reportMonth.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr);
      const daysInMonth = new Date(year, month, 0).getDate();

      // Pre-process logs into a map: "userId_YYYY-MM-DD" -> Log[]
      const logsMap = new Map<string, AttendanceLog[]>();
      logs.forEach(log => {
          const logDateStr = new Date(log.timestamp).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
          const key = `${log.userId}_${logDateStr}`;
          if (!logsMap.has(key)) logsMap.set(key, []);
          logsMap.get(key)?.push(log);
      });

      // Pre-process leaves: "userId" -> Leave[]
      const leavesMap = new Map<string, LeaveRecord[]>();
      leaves.forEach(l => {
          if (!leavesMap.has(l.userId)) leavesMap.set(l.userId, []);
          leavesMap.get(l.userId)?.push(l);
      });
      
      return sortedUsers.map(user => {
          let totalWorkDays = 0;
          let totalMissingMin = 0;
          let totalOvertimeMin = 0;
          let totalHolidayOvertimeMin = 0;
          
          for (let d = 1; d <= daysInMonth; d++) {
             const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
             
             // FAST LOOKUP
             const dailyLogs = logsMap.get(`${user.id}_${dateStr}`) || [];
             dailyLogs.sort((a,b) => a.timestamp - b.timestamp);

             const firstEntry = dailyLogs.find(l => l.type === LogType.ENTRY);
             const lastExit = [...dailyLogs].reverse().find(l => l.type === LogType.EXIT);
             
             let breakMin = 0;
             for(let i=0; i<dailyLogs.length-1; i++) {
                 if(dailyLogs[i].type === LogType.EXIT && dailyLogs[i+1].type === LogType.ENTRY) {
                     const e = getMinutesFromTimestamp(dailyLogs[i].timestamp);
                     const n = getMinutesFromTimestamp(dailyLogs[i+1].timestamp);
                     if(n > e) breakMin += (n - e);
                 }
             }

             const isHoliday = config.publicHolidays?.includes(dateStr);
             const dateObj = new Date(year, month - 1, d);
             const dayOfWeek = dateObj.getDay();
             const isSunday = dayOfWeek === 0;
             const isSaturday = dayOfWeek === 6;
             const isSatWorkDay = user.shiftConfig?.isSaturdayWorkDay ?? config.isSaturdayWorkDay;
             const isFullOvertimeDay = isHoliday || isSunday || (isSaturday && !isSatWorkDay);

             if(firstEntry && lastExit) {
                 totalWorkDays++;
                 const eMin = getMinutesFromTimestamp(firstEntry.timestamp);
                 const xMin = getMinutesFromTimestamp(lastExit.timestamp);
                 const duration = xMin - eMin;
                 
                 if(isFullOvertimeDay) {
                     const net = duration - breakMin;
                     if(isHoliday) totalHolidayOvertimeMin += net;
                     else totalOvertimeMin += net;
                 } else {
                     const sStart = user.shiftConfig?.start || config.shiftStart;
                     const sEnd = user.shiftConfig?.end || config.shiftEnd;
                     const tStart = timeStringToMinutes(sStart);
                     const tEnd = timeStringToMinutes(sEnd);
                     
                     let late = 0, early = 0;
                     if(eMin > tStart) late = eMin - tStart;
                     if(xMin < tEnd) early = tEnd - xMin;
                     totalMissingMin += (late + early + breakMin);
                     if(xMin > tEnd) totalOvertimeMin += (xMin - tEnd);
                 }
             }
          }
          
          return {
              user,
              totalWorkDays,
              totalMissingMin,
              totalOvertimeMin,
              totalHolidayOvertimeMin
          };
      });
  }, [sortedUsers, logs, config, reportMonth, leaves]);


  useEffect(() => { loadData(); }, [activeTab, reportMonth, salaryMonth]); // Re-load when month changes
  
  useEffect(() => {
      if(config) setConfigForm({ 
          companyName: config.companyName, 
          logoUrl: config.logoUrl || '',
          shiftStart: config.shiftStart || "08:00", 
          shiftEnd: config.shiftEnd || "18:00", 
          isSaturdayWorkDay: config.isSaturdayWorkDay || false, 
          saturdayStart: config.saturdayStart || "09:00",
          saturdayEnd: config.saturdayEnd || "14:00",
          publicHolidays: config.publicHolidays || [], 
          departments: config.departments || [], 
          qrRefreshSeconds: config.qrRefreshSeconds || 15 
      });
  }, [config]);

  useEffect(() => { if(settingsTab === 'data') loadCloudBackups(); }, [settingsTab]);
  useEffect(() => { setAdminCreds({ username: currentUser.username, password: currentUser.password }); }, [currentUser]);

  useEffect(() => {
      let interval: any;
      if (activeTab === 'settings') {
          const sync = async () => { const t = await storage.fetchRealTime(); setServerTime(t); };
          sync(); interval = setInterval(sync, 10000);
      }
      return () => clearInterval(interval);
  }, [activeTab]);

  useEffect(() => {
      const backupInterval = setInterval(() => { storage.checkAndTriggerAutoBackup(); }, 60000);
      return () => clearInterval(backupInterval);
  }, []);

  const loadData = async () => { 
      try {
          // Always fetch users and config as they are small and needed everywhere
          const [u, c, lev, notifs] = await Promise.all([storage.getUsers(), storage.getConfig(), storage.getLeaves(), storage.getNotifications()]);
          setUsers(u); setConfig(c); setLeaves(lev); setNotifications(notifs);

          // SMART FETCH FOR LOGS
          // Instead of fetching all logs forever, we fetch based on the active tab context.
          if (activeTab === 'dashboard') {
              // For dashboard we just need recent logs (3 days)
              const end = Date.now();
              const start = end - (3 * 24 * 60 * 60 * 1000);
              const recentLogs = await storage.getLogsByDateRange(start, end);
              setLogs(recentLogs);
          } else if (activeTab === 'users') {
              // For Users tab (to see history when clicking Detail), fetch last 90 days to cover recent months
              const end = Date.now();
              const start = end - (90 * 24 * 60 * 60 * 1000); 
              const recentLogs = await storage.getLogsByDateRange(start, end);
              setLogs(recentLogs);
          } else if (activeTab === 'reports') {
              // Fetch logs for the FULL YEAR of the selected report month to allow modal navigation
              const [year] = reportMonth.split('-').map(Number);
              const start = new Date(year, 0, 1).getTime(); // Jan 1st
              const end = new Date(year, 11, 31, 23, 59, 59).getTime(); // Dec 31st
              const monthLogs = await storage.getLogsByDateRange(start, end);
              setLogs(monthLogs);
          } else if (activeTab === 'salary') {
              // Fetch logs for the selected Salary Month
              const [year, month] = salaryMonth.split('-').map(Number);
              const start = new Date(year, month - 1, 1).getTime();
              const end = new Date(year, month, 0, 23, 59, 59).getTime();
              const monthLogs = await storage.getLogsByDateRange(start, end);
              setLogs(monthLogs);
          } else {
              // For other tabs (Settings, Notifications), we might not need logs or just recent ones
              const recentLogs = await storage.getLogs(); // fallback default limited
              setLogs(recentLogs);
          }

      } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  
  // --- AUTOMATIC SALARY CALCULATION EFFECT ---
  useEffect(() => {
      if (activeTab === 'salary' && users.length > 0 && config) {
          const fetchAndCalculate = async () => {
              const existingRecords = await storage.getSalaryRecords(salaryMonth);
              recalculateSalaries(existingRecords);
          };
          fetchAndCalculate();
      }
  }, [activeTab, salaryMonth, users, logs, leaves, config]);


  const loadCloudBackups = async () => { try { setCloudBackups(await storage.getCloudBackups()); } catch(e) { console.error(e); } };
  
  const handleSaveUser = async (user: User) => { try { if (user.id) await storage.updateUser(user); else await storage.saveUser(user); setIsUserModalOpen(false); setEditingUser(null); loadData(); } catch(e) { alert("Hata"); } };
  const handleDeleteUser = async (id: string) => { await storage.deleteUser(id); setDeleteModalId(null); loadData(); };

  const handleManualLogUpdate = async (userId: string, dateStr: string, type: LogType, timeStr: string) => {
      if(!timeStr) return;
      const [h, m] = timeStr.split(':').map(Number);
      const [year, month, day] = dateStr.split('-').map(Number);
      const newTimestamp = new Date(year, month - 1, day, h, m).getTime();

      const existingLog = logs.find(l => {
          const logDateStr = new Date(l.timestamp).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
          return l.userId === userId && l.type === type && logDateStr === dateStr;
      });

      if (existingLog) await storage.updateLog({ ...existingLog, timestamp: newTimestamp });
      else { const user = users.find(u => u.id === userId); await storage.addLog(userId, user?.fullName || 'Unknown', type, newTimestamp, 'Manual Edit', 'Admin Panel'); }
      await loadData(); 
  };

  const departmentList = useMemo(() => config?.departments || [], [config]);
  const handleConfigUpdate = async (newConfig: AppConfig) => { try { await storage.updateConfig(newConfig); setConfig(newConfig); alert("Ayarlar güncellendi."); } catch (e) { console.error(e); alert("Hata oluştu"); } };
  const handleSettingsSave = async (e?: React.FormEvent) => { if(e) e.preventDefault(); if(!config) return; await handleConfigUpdate({ ...config, ...configForm }); };
  const addHoliday = () => { if (!newHoliday) return; const currentHolidays = configForm.publicHolidays || []; if (!currentHolidays.includes(newHoliday)) setConfigForm({ ...configForm, publicHolidays: [...currentHolidays, newHoliday].sort() }); setNewHoliday(''); };
  const autoAddHolidays = () => { const h = storage.getTurkishHolidays(new Date().getFullYear()); const current = new Set(configForm.publicHolidays || []); h.forEach(date => current.add(date)); setConfigForm({...configForm, publicHolidays: Array.from(current).sort()}); };
  const removeHoliday = (date: string) => { const currentHolidays = configForm.publicHolidays || []; setConfigForm({ ...configForm, publicHolidays: currentHolidays.filter(h => h !== date) }); };
  const handleAddDepartment = () => { if(!newDeptName.trim()) return; const currentDepts = configForm.departments || []; if (!currentDepts.includes(newDeptName)) { setConfigForm({ ...configForm, departments: [...currentDepts, newDeptName] }); } setNewDeptName(''); };
  const removeDepartment = (dept: string) => { const currentDepts = configForm.departments || []; setConfigForm({ ...configForm, departments: currentDepts.filter(d => d !== dept)}); }
  const regenerateStaticQR = async () => { if(!confirm("Bu işlem mevcut sabit QR kodunu geçersiz kılar ve yenisini oluşturur.")) return; const newSecret = `TECHNOSIS_STATIC_${Date.now()}_${Math.random().toString(36).substring(2, 9).toUpperCase()}`; if(config) { const updated = { ...config, qrSecret: newSecret }; await storage.updateConfig(updated); setConfig(updated); alert("Yeni QR Kodu oluşturuldu."); } };
  
  const handleCreateCloudBackup = async () => { 
      setIsCloudLoading(true); 
      setCloudStatusMessage("Yedekleme başlatılıyor...");
      try { 
          await storage.saveCloudBackup('MANUAL', (msg) => setCloudStatusMessage(msg)); 
          setCloudBackups(await storage.getCloudBackups()); 
          alert("Yedekleme tamamlandı."); 
      } catch(e) { 
          alert("Hata oluştu: " + e); 
      } 
      setIsCloudLoading(false); 
      setCloudStatusMessage("");
  };

  const handleRestoreCloud = async (id: string) => { 
      if(!confirm("Bu yedeği geri yüklemek mevcut verileri değiştirebilir. Emin misiniz?")) return; 
      setIsCloudLoading(true); 
      setCloudStatusMessage("Geri yükleniyor...");
      
      const res = await storage.restoreFromCloud(id, (msg) => setCloudStatusMessage(msg)); 
      
      setIsCloudLoading(false); 
      setCloudStatusMessage("");
      alert(res.message); 
      if(res.success) loadData(); 
  };

  const handleDeleteCloud = async (id: string) => { if(!confirm("Silmek istediğinize emin misiniz?")) return; await storage.deleteCloudBackup(id); setCloudBackups(await storage.getCloudBackups()); };
  const handleDownloadBackup = async () => { setIsBackingUp(true); try { const data = await storage.createFullBackup(); const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `technosis_backup_${new Date().toISOString().split('T')[0]}.json`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); } catch(e) { alert("Yedekleme hatası"); } setIsBackingUp(false); };
  const handleRestoreBackup = async (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0]; if(!file) return; setIsRestoring(true); try { const text = await file.text(); const data = JSON.parse(text); const res = await storage.restoreFromBackup(data); alert(res.message); if(res.success) loadData(); } catch(err) { alert("Dosya okuma hatası"); } setIsRestoring(false); if(fileInputRef.current) fileInputRef.current.value = ''; };
  const handleDatabaseRepair = async () => { setIsRepairing(true); try { const res = await storage.fixDatabaseConsistency(); alert(`Onarım Tamamlandı: ${res.details}`); loadData(); } catch(e) { alert("Hata"); } setIsRepairing(false); };
  const handleUpdateKioskUser = async () => { if(!kioskPassword) return; await storage.updateKioskCredentials(kioskPassword); setKioskPassword(''); alert("Kiosk şifresi güncellendi."); }
  const handleUpdateAdmin = async () => { if(!adminCreds.username || !adminCreds.password) return; await storage.updateAdminCredentials(currentUser.id, adminCreds.username, adminCreds.password); alert("Yönetici bilgileri güncellendi. Lütfen tekrar giriş yapın."); onLogout(); };
  const handleNotifStatusChange = async (id: string, status: NotificationStatus) => { await storage.updateNotificationStatus(id, status); setNotifications(prev => prev.map(n => n.id === id ? { ...n, status } : n)); };
  const handleDeleteNotif = async (id: string) => { if(!confirm("Silmek istediğinize emin misiniz?")) return; await storage.deleteNotification(id); setNotifications(prev => prev.filter(n => n.id !== id)); };

  const userStatusMap = useMemo(() => {
      const status: Record<string, { isInside: boolean, lastLogTime: number, isOnLeave: boolean }> = {};
      const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
      users.forEach(u => {
          const userLogs = logs.filter(l => l.userId === u.id).sort((a,b) => b.timestamp - a.timestamp);
          const isOnLeave = leaves.some(l => l.userId === u.id && l.startDate <= todayStr && l.endDate >= todayStr);
          status[u.id] = { 
              isInside: userLogs.length > 0 && userLogs[0].type === LogType.ENTRY, 
              lastLogTime: userLogs.length > 0 ? userLogs[0].timestamp : 0,
              isOnLeave
          };
      });
      return status;
  }, [users, logs, leaves]);

  // ... (Salary Calculation Logic remains identical) ...
  const recalculateSalaries = (dbRecords: SalaryRecord[] = []) => {
      if(!users.length || !config) return;
      const [yearStr, monthStr] = salaryMonth.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr);
      const daysInMonth = new Date(year, month, 0).getDate();
      const now = new Date();
      const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month;
      
      const activeSortedUsers = sortedUsers.filter(u => !u.isExcludedFromSalary);

      // Pre-process Logs for Salary Calc (O(N) instead of O(N^2))
      const logsMap = new Map<string, AttendanceLog[]>();
      logs.forEach(log => {
          const logDateStr = new Date(log.timestamp).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
          const key = `${log.userId}_${logDateStr}`;
          if (!logsMap.has(key)) logsMap.set(key, []);
          logsMap.get(key)?.push(log);
      });

      const newRecords: SalaryRecord[] = activeSortedUsers.map(user => {
          const existing = dbRecords.find(r => r.userId === user.id);
          const baseSalary = parseSalary(user.salary);
          const dailyRate = baseSalary / 30;
          const hourlyRate = dailyRate / 7.5;
          
          let totalOvertimeMin = 0;
          let totalHolidayMin = 0;
          let totalMissingMin = 0;
          let totalMissingDays = 0;

          for(let d=1; d<=daysInMonth; d++) {
               const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
               const loopDate = new Date(year, month - 1, d);
               if (isCurrentMonth && loopDate > now) continue;
               const dayOfWeek = loopDate.getDay();
               
               const dailyLogs = logsMap.get(`${user.id}_${dateStr}`) || [];
               dailyLogs.sort((a,b) => a.timestamp - b.timestamp);

               const firstEntry = dailyLogs.find(l => l.type === LogType.ENTRY);
               const lastExit = [...dailyLogs].reverse().find(l => l.type === LogType.EXIT);
               
               let breakMin = 0;
               for(let i=0; i<dailyLogs.length-1; i++) {
                  if(dailyLogs[i].type === LogType.EXIT && dailyLogs[i+1].type === LogType.ENTRY) {
                       const eT = getMinutesFromTimestamp(dailyLogs[i].timestamp);
                       const nT = getMinutesFromTimestamp(dailyLogs[i+1].timestamp);
                       if(nT > eT) breakMin += (nT - eT);
                  }
               }
               
               const isHoliday = config.publicHolidays.includes(dateStr);
               const isSunday = dayOfWeek === 0;
               const isSaturday = dayOfWeek === 6;
               const isSatWorkDay = user.shiftConfig?.isSaturdayWorkDay ?? config.isSaturdayWorkDay;
               const isFullOvertimeDay = isHoliday || isSunday || (isSaturday && !isSatWorkDay);

               const isOnLeave = leaves.some(l => l.userId === user.id && l.startDate <= dateStr && l.endDate >= dateStr);
               
               if(isOnLeave) {
                  const leaveRec = leaves.find(l => l.userId === user.id && l.startDate <= dateStr && l.endDate >= dateStr);
                  if(leaveRec?.type === LeaveType.UNPAID) totalMissingDays++;
               } 
               else if (firstEntry && lastExit) {
                   const eMin = getMinutesFromTimestamp(firstEntry.timestamp);
                   const xMin = getMinutesFromTimestamp(lastExit.timestamp);
                   const dur = xMin - eMin;
                   if (isFullOvertimeDay) {
                       const netDur = dur - breakMin;
                       if (isHoliday || isSunday) totalHolidayMin += netDur;
                       else totalOvertimeMin += netDur; 
                   } else {
                       const sStart = user.shiftConfig?.start || config.shiftStart;
                       const sEnd = user.shiftConfig?.end || config.shiftEnd;
                       const tStart = timeStringToMinutes(sStart);
                       const tEnd = timeStringToMinutes(sEnd);
                       let l = 0, e = 0;
                       if(eMin > tStart) l = eMin - tStart;
                       if(xMin < tEnd) e = tEnd - xMin;
                       totalMissingMin += (l + e + breakMin);
                       if(xMin > tEnd) totalOvertimeMin += (xMin - tEnd); 
                   }
               } else if (!isFullOvertimeDay && dateStr <= new Date().toISOString().split('T')[0]) {
                   totalMissingDays++;
               }
          }

          if (user.isFixedSalary) {
              totalOvertimeMin = 0; totalHolidayMin = 0; totalMissingMin = 0; totalMissingDays = 0;
          }

          const overtimePay = (totalOvertimeMin / 60) * (hourlyRate * 1.5);
          const holidayPay = (totalHolidayMin / 60) * (hourlyRate * 2);
          const missingPay = (totalMissingMin / 60) * hourlyRate;
          const missingDaysPay = totalMissingDays * dailyRate;
          
          const extra = existing?.extra || 0;
          const totalPay = baseSalary + overtimePay + holidayPay - missingPay - missingDaysPay + extra;
          const defaultBank = Math.min(baseSalary, totalPay);
          const bankPay = existing ? existing.bankPay : defaultBank;
          const cashPay = totalPay - bankPay;

          return {
              id: `${user.id}_${salaryMonth}`,
              userId: user.id,
              userFullName: user.fullName,
              month: salaryMonth,
              baseSalary, hourlyRate,
              overtimeHours: totalOvertimeMin / 60, overtimePay,
              holidayHours: totalHolidayMin / 60, holidayPay,
              missingHours: totalMissingMin / 60, missingPay,
              missingDays: totalMissingDays, missingDaysPay,
              extra, totalPay, bankPay, cashPay,
              updatedAt: Date.now()
          };
      });
      setSalaryRecords(newRecords);
  };

  const updateSalaryRecord = (userId: string, field: 'extra' | 'bankPay', value: number) => {
      setSalaryRecords(prev => prev.map(rec => {
          if (rec.userId === userId) {
              const updated = { ...rec };
              if (field === 'extra') updated.extra = value;
              if (field === 'bankPay') updated.bankPay = value;
              updated.totalPay = updated.baseSalary + updated.overtimePay + updated.holidayPay - updated.missingPay - updated.missingDaysPay + updated.extra;
              updated.cashPay = updated.totalPay - updated.bankPay;
              return updated;
          }
          return rec;
      }));
  };

  const saveSalaries = async () => {
      if(!confirm("Maaş tablosundaki değişiklikleri kaydetmek istiyor musunuz?")) return;
      for (const rec of salaryRecords) await storage.saveSalaryRecord(rec);
      alert("Kayıtlar güncellendi.");
  };

  const handleResetMonth = async () => {
      if(!confirm("Bu aya ait kayıtlı tüm maaş verileri (ekstra ödemeler dahil) SİLİNECEK ve yeniden hesaplanacak. Emin misiniz?")) return;
      try {
          await storage.deleteSalaryRecordsForMonth(salaryMonth);
          recalculateSalaries([]); 
          alert("Ay sıfırlandı ve yeniden hesaplandı.");
      } catch (e) { console.error(e); alert("Sıfırlama sırasında hata oluştu."); }
  };

  const downloadSalaryPDF = () => {
      const doc = new jsPDF('l', 'mm', 'a4');
      doc.setFontSize(18);
      doc.setTextColor(40);
      doc.text(normalizeTR(config?.companyName || "Firma Adi"), 14, 15);
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(normalizeTR(`Maaş Bordrosu - ${salaryMonth}`), 14, 22);
      doc.setFontSize(10);
      doc.text(normalizeTR(`Oluşturulma Tarihi: ${new Date().toLocaleDateString('tr-TR')}`), 250, 15, { align: 'right' });

      const tableColumn = [ "Personel", "Baz Maas", "1.5x Saat", "1.5x Tutar", "2x Saat", "2x Tutar", "Kesinti", "Ekstra", "Hakedis", "Banka", "Elden" ];
      const tableRows = salaryRecords.map(rec => [
          normalizeTR(rec.userFullName),
          formatCurrency(rec.baseSalary).replace('₺', 'TL'), 
          rec.overtimeHours.toFixed(2), formatCurrency(rec.overtimePay).replace('₺', 'TL'),
          rec.holidayHours.toFixed(2), formatCurrency(rec.holidayPay).replace('₺', 'TL'),
          formatCurrency(rec.missingPay + rec.missingDaysPay).replace('₺', 'TL'),
          formatCurrency(rec.extra).replace('₺', 'TL'),
          formatCurrency(rec.totalPay).replace('₺', 'TL'),
          formatCurrency(rec.bankPay).replace('₺', 'TL'),
          formatCurrency(rec.cashPay).replace('₺', 'TL')
      ]);
      
      const totalHakedis = salaryRecords.reduce((sum, r) => sum + r.totalPay, 0);
      const totalBanka = salaryRecords.reduce((sum, r) => sum + r.bankPay, 0);
      const totalElden = salaryRecords.reduce((sum, r) => sum + r.cashPay, 0);

      tableRows.push([ "GENEL TOPLAM", "-", "-", "-", "-", "-", "-", "-", formatCurrency(totalHakedis).replace('₺', 'TL'), formatCurrency(totalBanka).replace('₺', 'TL'), formatCurrency(totalElden).replace('₺', 'TL') ]);

      (doc as any).autoTable({
          head: [tableColumn], body: tableRows, startY: 30, theme: 'striped', styles: { fontSize: 8, cellPadding: 3 }, headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold' },
          columnStyles: { 0: { fontStyle: 'bold' }, 8: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
          didParseCell: (data: any) => { if (data.row.index === tableRows.length - 1) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.fillColor = [200, 200, 200]; } }
      });

      const finalY = (doc as any).lastAutoTable.finalY || 150;
      doc.setFontSize(10); doc.setTextColor(0);
      doc.text("Muhasebe Onay", 50, finalY + 20); doc.text("..................................", 50, finalY + 30);
      doc.text("Isveren / Yonetici Onay", 200, finalY + 20); doc.text("..................................", 200, finalY + 30);
      doc.save(`Maas_Bordrosu_${salaryMonth}.pdf`);
  };

  if (loading && !config) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="w-10 h-10 animate-spin text-blue-500" /></div>;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans pb-20">
       <UserModal isOpen={isUserModalOpen} onClose={() => { setIsUserModalOpen(false); setEditingUser(null); }} onSave={handleSaveUser} user={editingUser} departments={departmentList} />
       <DeleteConfirmModal isOpen={!!deleteModalId} onClose={() => setDeleteModalId(null)} onConfirm={() => deleteModalId && handleDeleteUser(deleteModalId)} title="Personel Silinecek" message="Bu personeli ve ilişkili tüm verileri silmek istediğinize emin misiniz?" />
       <UserLeavesModal isOpen={!!leavesModalUser} onClose={() => setLeavesModalUser(null)} user={leavesModalUser} />
       <UserReportModal isOpen={!!reportModalUser} onClose={() => setReportModalUser(null)} user={reportModalUser} logs={logs.filter(l => l.userId === reportModalUser?.id)} leaves={leaves.filter(l => l.userId === reportModalUser?.id)} config={config} initialMonth={reportMonth} onUpdateLog={handleManualLogUpdate} />

      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-md bg-opacity-90">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
                <div className="bg-blue-600 p-2.5 rounded-xl shadow-lg shadow-blue-900/50"><Briefcase className="w-6 h-6 text-white" /></div>
                <div><h1 className="text-xl font-bold tracking-tight text-white leading-tight">Yönetim Paneli</h1><p className="text-slate-400 text-xs font-medium">{config?.companyName}</p></div>
            </div>
            <div className="flex items-center gap-4">
                 <div className="text-right hidden sm:block"><div className="font-bold text-sm text-slate-200">{currentUser.fullName}</div><div className="text-[10px] uppercase tracking-wider text-blue-400 font-bold">Yönetici</div></div>
                 <button onClick={onLogout} className="p-2.5 bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors border border-slate-700 group"><LogOut className="w-5 h-5 text-slate-400 group-hover:text-red-400 transition-colors" /></button>
            </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 mt-2">
            <div className="flex space-x-1 overflow-x-auto pb-0 scrollbar-hide">
                {[ { id: 'dashboard', label: 'Genel', icon: PieChart }, { id: 'users', label: 'Personel', icon: Users }, { id: 'reports', label: 'Raporlar', icon: FileSpreadsheet }, { id: 'salary', label: 'Maaş', icon: Banknote }, { id: 'notifications', label: 'Bildirimler', icon: Bell }, { id: 'settings', label: 'Ayarlar', icon: Settings } ].map((tab) => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`flex items-center px-5 py-3 rounded-t-xl font-bold text-sm transition-all duration-200 border-b-2 ${activeTab === tab.id ? 'bg-slate-800/50 text-blue-400 border-blue-500' : 'text-slate-500 border-transparent hover:text-slate-300 hover:bg-slate-800/30'}`}>
                        <tab.icon className={`w-4 h-4 mr-2 ${activeTab === tab.id ? 'text-blue-400' : 'text-slate-600'}`} /> {tab.label}
                        {tab.id === 'notifications' && notifications.filter(n => n.status === NotificationStatus.PENDING).length > 0 && (<span className="ml-2 bg-red-500 text-white text-[9px] px-1.5 py-0.5 rounded-full">{notifications.filter(n => n.status === NotificationStatus.PENDING).length}</span>)}
                    </button>
                ))}
            </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        
        {/* ... (Existing Tabs: Dashboard, Users) ... */}
        {activeTab === 'dashboard' && (
             <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                     {[ { title: "Toplam Personel", val: users.length, icon: Users, color: "text-blue-500", bg: "bg-blue-500/10" }, { title: "İçeride", val: users.filter(u => userStatusMap[u.id]?.isInside).length, icon: UserCheck, color: "text-green-500", bg: "bg-green-500/10" }, { title: "Dışarıda", val: users.filter(u => !userStatusMap[u.id]?.isInside && !userStatusMap[u.id]?.isOnLeave).length, icon: LogOut, color: "text-orange-500", bg: "bg-orange-500/10" }, { title: "İzinli", val: users.filter(u => userStatusMap[u.id]?.isOnLeave).length, icon: Plane, color: "text-purple-500", bg: "bg-purple-500/10" }, ].map((stat, i) => (
                    <div key={i} className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800"><div className="flex justify-between items-center"><div><p className="text-xs font-bold text-slate-500 uppercase">{stat.title}</p><h3 className="text-3xl font-black mt-1 text-white">{stat.val}</h3></div><div className={`p-3 rounded-xl ${stat.bg} ${stat.color}`}><stat.icon className="w-6 h-6" /></div></div></div>
                 ))}
                 </div>
                 {/* Sorted Users for Dashboard Widgets handled by default sort or just filtered lists, no sort needed here specifically unless requested, keeping as is for performance or simple filtering */}
                 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                     <div className="bg-slate-900 rounded-2xl shadow-lg border border-slate-800 p-5 flex flex-col h-[500px]">
                         <h3 className="text-green-400 font-bold mb-4 flex items-center gap-2 border-b border-slate-800 pb-2"><div className="w-2 h-2 rounded-full bg-green-500"></div> İçerideki Personel</h3>
                         <div className="flex-1 overflow-auto space-y-2 pr-2">{sortedUsers.filter(u => userStatusMap[u.id]?.isInside).map(u => (<div key={u.id} className="p-3 bg-slate-950/50 rounded-xl border border-slate-800 flex justify-between items-center"><div><div className="font-bold text-sm text-white">{u.fullName}</div><div className="text-[10px] text-slate-500">{u.department}</div></div><div className="text-xs font-mono text-green-500">{formatTimeTR(userStatusMap[u.id].lastLogTime)}</div></div>))}</div>
                     </div>
                     <div className="bg-slate-900 rounded-2xl shadow-lg border border-slate-800 p-5 flex flex-col h-[500px]">
                         <h3 className="text-orange-400 font-bold mb-4 flex items-center gap-2 border-b border-slate-800 pb-2"><div className="w-2 h-2 rounded-full bg-orange-500"></div> Dışarıdaki Personel</h3>
                         <div className="flex-1 overflow-auto space-y-2 pr-2">{sortedUsers.filter(u => !userStatusMap[u.id]?.isInside && !userStatusMap[u.id]?.isOnLeave).map(u => (<div key={u.id} className="p-3 bg-slate-950/50 rounded-xl border border-slate-800 flex justify-between items-center"><div><div className="font-bold text-sm text-white">{u.fullName}</div><div className="text-[10px] text-slate-500">{u.department}</div></div></div>))}</div>
                     </div>
                     <div className="bg-slate-900 rounded-2xl shadow-lg border border-slate-800 p-5 flex flex-col h-[500px]">
                         <h3 className="text-purple-400 font-bold mb-4 flex items-center gap-2 border-b border-slate-800 pb-2"><div className="w-2 h-2 rounded-full bg-purple-500"></div> İzinli Personel</h3>
                         <div className="flex-1 overflow-auto space-y-2 pr-2">{sortedUsers.filter(u => userStatusMap[u.id]?.isOnLeave).map(u => (<div key={u.id} className="p-3 bg-slate-950/50 rounded-xl border border-slate-800 flex justify-between items-center"><div><div className="font-bold text-sm text-white">{u.fullName}</div><div className="text-[10px] text-slate-500">{u.department}</div></div><div className="px-2 py-1 bg-purple-500/20 text-purple-400 text-[10px] rounded font-bold">İZİNLİ</div></div>))}</div>
                     </div>
                 </div>
             </div>
        )}
        
        {/* Reports Tab Fixed */}
        {activeTab === 'reports' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800">
                     <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-white flex items-center gap-2"><FileSpreadsheet className="w-6 h-6 text-blue-500" /> Aylık Genel Rapor</h2>
                            <p className="text-sm text-slate-500 mt-1">Seçili ay için tüm personelin özet durumunu görüntülüyorsunuz. Detaylar için ilgili satırdaki butonu kullanın.</p>
                        </div>
                        <div className="flex gap-2">
                             <input type="month" value={reportMonth} onChange={e => setReportMonth(e.target.value)} className="bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-white font-bold text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-slate-800">
                        <table className="w-full text-sm text-left text-slate-400">
                            <thead className="bg-slate-950 text-xs uppercase font-bold text-slate-300">
                                <tr>
                                    <th className="p-4 whitespace-nowrap">Personel</th>
                                    <th className="p-4 text-center whitespace-nowrap bg-blue-900/10">Çalışılan Gün</th>
                                    <th className="p-4 text-center whitespace-nowrap bg-red-900/10">Eksik Süre</th>
                                    <th className="p-4 text-center whitespace-nowrap bg-green-900/10">Fazla Mesai</th>
                                    <th className="p-4 text-center whitespace-nowrap bg-purple-900/10">Bayram Mesai</th>
                                    <th className="p-4 text-right whitespace-nowrap">İşlem</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800">
                                {monthlyStats.length === 0 && (
                                    <tr><td colSpan={6} className="p-8 text-center text-slate-500">Kayıt bulunamadı.</td></tr>
                                )}
                                {monthlyStats.map((stat, idx) => (
                                    <tr key={idx} className="hover:bg-slate-800/40 transition-colors">
                                        <td className="p-4">
                                            <div className="font-bold text-white">{stat.user.fullName}</div>
                                            <div className="text-xs text-slate-500">{stat.user.department}</div>
                                        </td>
                                        <td className="p-4 text-center font-bold text-white bg-blue-900/5">{stat.totalWorkDays} Gün</td>
                                        <td className="p-4 text-center font-bold text-red-400 bg-red-900/5">{minutesToDuration(stat.totalMissingMin)}</td>
                                        <td className="p-4 text-center font-bold text-green-400 bg-green-900/5">{minutesToDuration(stat.totalOvertimeMin)}</td>
                                        <td className="p-4 text-center font-bold text-purple-400 bg-purple-900/5">{minutesToDuration(stat.totalHolidayOvertimeMin)}</td>
                                        <td className="p-4 text-right">
                                            <button onClick={() => setReportModalUser(stat.user)} className="px-3 py-1.5 bg-slate-800 hover:bg-blue-600 hover:text-white text-blue-400 rounded-lg text-xs font-bold transition-all border border-slate-700 hover:border-blue-500 flex items-center gap-2 ml-auto">
                                                <FileSpreadsheet className="w-3 h-3"/> Detay
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}
        
        {activeTab === 'salary' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-white flex items-center gap-2"><Banknote className="w-6 h-6 text-green-500" /> Maaş & Hakediş Hesaplama</h2>
                            <p className="text-sm text-slate-500 mt-1">Sistem, log kayıtlarına göre maaşları <span className="text-green-400 font-bold">otomatik hesaplar</span>. Değişiklikleri kaydetmeyi unutmayın.</p>
                        </div>
                        <div className="flex gap-2">
                            <input type="month" value={salaryMonth} onChange={e => setSalaryMonth(e.target.value)} className="bg-slate-950 border border-slate-700 rounded-lg p-2.5 text-white font-bold text-sm" />
                        </div>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-slate-800">
                        <table className="w-full text-sm text-left text-slate-400">
                            <thead className="bg-slate-950 text-xs uppercase font-bold text-slate-300">
                                <tr>
                                    <th className="p-4 whitespace-nowrap">Personel</th>
                                    <th className="p-4 text-right whitespace-nowrap">Baz Maaş</th>
                                    <th className="p-4 text-center whitespace-nowrap bg-blue-900/10">1.5x Mesai</th>
                                    <th className="p-4 text-center whitespace-nowrap bg-purple-900/10">2x Mesai</th>
                                    <th className="p-4 text-center whitespace-nowrap bg-red-900/10">Kesintiler (Saat/Gün)</th>
                                    <th className="p-4 text-right whitespace-nowrap w-24">Ekstra</th>
                                    <th className="p-4 text-right whitespace-nowrap font-black text-white bg-slate-800">Hakediş</th>
                                    <th className="p-4 text-right whitespace-nowrap w-24">Banka</th>
                                    <th className="p-4 text-right whitespace-nowrap">Elden</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800">
                                {salaryRecords.length === 0 && (
                                    <tr><td colSpan={9} className="p-8 text-center text-slate-500">Bu ay için hesaplanacak veri bulunamadı veya tüm personeller gizli.</td></tr>
                                )}
                                {salaryRecords.map(rec => (
                                    <tr key={rec.id} className="hover:bg-slate-800/40 transition-colors">
                                        <td className="p-4 font-bold text-white">
                                            {rec.userFullName}
                                            {users.find(u => u.id === rec.userId)?.isFixedSalary && <span className="ml-2 text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded font-bold uppercase">SABİT</span>}
                                        </td>
                                        <td className="p-4 text-right font-mono">{formatCurrency(rec.baseSalary)}</td>
                                        
                                        <td className="p-4 text-center bg-blue-900/5">
                                            <div className="font-bold text-blue-400">{rec.overtimeHours.toFixed(1)} sa</div>
                                            <div className="text-xs text-slate-500">{formatCurrency(rec.overtimePay)}</div>
                                        </td>
                                        
                                        <td className="p-4 text-center bg-purple-900/5">
                                            <div className="font-bold text-purple-400">{rec.holidayHours.toFixed(1)} sa</div>
                                            <div className="text-xs text-slate-500">{formatCurrency(rec.holidayPay)}</div>
                                        </td>
                                        
                                        <td className="p-4 text-center bg-red-900/5">
                                            <div className="flex flex-col gap-1 items-center">
                                                <div className="flex gap-2">
                                                    {rec.missingHours > 0 && <span className="text-xs text-red-400 font-bold">{rec.missingHours.toFixed(1)} sa</span>}
                                                    {rec.missingDays > 0 && <span className="text-xs text-red-500 font-black">{rec.missingDays} gün</span>}
                                                </div>
                                                <span className="text-xs text-slate-500">-{formatCurrency(rec.missingPay + rec.missingDaysPay)}</span>
                                            </div>
                                        </td>
                                        
                                        <td className="p-4">
                                            <input type="number" className="w-full bg-slate-950 border border-slate-700 rounded p-1.5 text-right text-white text-xs font-mono focus:border-blue-500 outline-none transition-colors" value={rec.extra} onChange={e => updateSalaryRecord(rec.userId, 'extra', Number(e.target.value))} />
                                        </td>
                                        
                                        <td className="p-4 text-right font-black text-green-400 text-base bg-slate-800/50 border-x border-slate-700">
                                            {formatCurrency(rec.totalPay)}
                                        </td>
                                        
                                        <td className="p-4">
                                            <input type="number" className="w-full bg-slate-950 border border-slate-700 rounded p-1.5 text-right text-white text-xs font-mono focus:border-blue-500 outline-none transition-colors" value={rec.bankPay} onChange={e => updateSalaryRecord(rec.userId, 'bankPay', Number(e.target.value))} />
                                        </td>
                                        
                                        <td className="p-4 text-right font-mono text-slate-300">
                                            {formatCurrency(rec.cashPay)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 flex justify-end gap-4 border-t border-slate-800 pt-4">
                        <button onClick={handleResetMonth} className="px-6 py-3 bg-red-900/20 text-red-400 border border-red-900/50 hover:bg-red-900/30 rounded-xl font-bold flex items-center gap-2 mr-auto">
                            <RotateCcw className="w-5 h-5"/> Ayı Sıfırla
                        </button>
                        <button onClick={downloadSalaryPDF} className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold flex items-center gap-2 border border-slate-700">
                            <Download className="w-5 h-5"/> PDF İndir
                        </button>
                        <button onClick={saveSalaries} className="px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-green-900/20">
                            <Save className="w-5 h-5"/> Değişiklikleri Kaydet
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* ... (Notifications and Users tabs remain essentially the same layout, just simplified data loading) ... */}
        {/* We keep the rest of the file content for Users, Notifications, Settings tabs as they are handled by logic above */}
        {activeTab === 'notifications' && (
             <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Bell className="w-5 h-5 text-yellow-500" /> Bildirimler & Talepler</h2>
                        <div className="flex bg-slate-950 rounded-lg p-1 border border-slate-800">
                            {/* FIX: Use Enum values for setting filter */}
                            <button onClick={() => setNotifFilterStatus(NotificationStatus.PENDING)} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${notifFilterStatus === NotificationStatus.PENDING ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-300'}`}>Bekleyenler</button>
                            <button onClick={() => setNotifFilterStatus(NotificationStatus.APPROVED)} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${notifFilterStatus === NotificationStatus.APPROVED ? 'bg-slate-800 text-green-500' : 'text-slate-500 hover:text-slate-300'}`}>Onaylananlar</button>
                            <button onClick={() => setNotifFilterStatus(NotificationStatus.REJECTED)} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${notifFilterStatus === NotificationStatus.REJECTED ? 'bg-slate-800 text-red-500' : 'text-slate-500 hover:text-slate-300'}`}>Reddedilenler</button>
                            <button onClick={() => setNotifFilterStatus('ALL')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${notifFilterStatus === 'ALL' ? 'bg-slate-800 text-blue-500' : 'text-slate-500 hover:text-slate-300'}`}>Tümü</button>
                        </div>
                    </div>
                    <div className="space-y-3">
                        {notifications.filter(n => notifFilterStatus === 'ALL' || n.status === notifFilterStatus).map(notif => (
                            <div key={notif.id} className={`p-5 rounded-2xl border flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-all hover:shadow-md ${notif.status === NotificationStatus.PENDING ? 'bg-slate-800/40 border-slate-700' : 'bg-slate-900 border-slate-800 opacity-70'}`}>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center font-bold text-white text-xs border border-slate-700">{notif.userFullName.charAt(0)}</div>
                                        <div><div className="text-white font-bold text-sm">{notif.userFullName}</div><div className="text-[10px] text-slate-500">{new Date(notif.createdAt).toLocaleString('tr-TR')}</div></div>
                                        <span className={`ml-2 px-2 py-0.5 rounded text-[10px] font-bold ${notif.type.includes('QR') ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>{notif.type}</span>
                                    </div>
                                    <div className="text-sm text-slate-300 pl-11 mb-2 bg-slate-950/50 p-2 rounded-lg border border-slate-800/50">"{notif.description}"</div>
                                    <div className="text-xs font-mono text-blue-400 pl-11 flex gap-4"><span className="flex items-center gap-1"><CalendarDays className="w-3 h-3"/> {new Date(notif.targetDate).toLocaleDateString('tr-TR')}</span></div>
                                </div>
                                {notif.status === NotificationStatus.PENDING ? (
                                    <div className="flex gap-2 w-full md:w-auto mt-2 md:mt-0">
                                        <button onClick={() => handleNotifStatusChange(notif.id, NotificationStatus.APPROVED)} className="flex-1 md:flex-none px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1 transition-colors"><Check className="w-4 h-4"/> Onayla</button>
                                        <button onClick={() => handleNotifStatusChange(notif.id, NotificationStatus.REJECTED)} className="flex-1 md:flex-none px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1 transition-colors"><XIcon className="w-4 h-4"/> Reddet</button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3 mt-2 md:mt-0 pl-11 md:pl-0">
                                        <span className={`text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 ${notif.status === NotificationStatus.APPROVED ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`}>{notif.status === NotificationStatus.APPROVED ? <Check className="w-3 h-3"/> : <XIcon className="w-3 h-3"/>} {notif.status}</span>
                                        <button onClick={() => handleDeleteNotif(notif.id)} className="text-slate-600 hover:text-red-500 transition-colors p-2 hover:bg-slate-800 rounded-lg"><Trash2 className="w-4 h-4"/></button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}

        {activeTab === 'users' && (
             <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                 <div className="bg-slate-900 p-5 rounded-2xl shadow-lg border border-slate-800 flex flex-wrap gap-4 items-center justify-between sticky top-24 z-30 backdrop-blur-md bg-opacity-95">
                     <div className="flex gap-4 items-center w-full md:w-auto flex-1">
                        <div className="relative flex-1 md:max-w-md group"><Search className="absolute left-3 top-3 w-5 h-5 text-slate-500 group-focus-within:text-blue-500 transition-colors" /><input type="text" placeholder="İsim, departman..." className="pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl w-full text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none" value={userSearch} onChange={e => setUserSearch(e.target.value)} /></div>
                        <select className="p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-300 focus:ring-2 focus:ring-blue-500 outline-none" value={userFilterDept} onChange={e => setUserFilterDept(e.target.value)}><option value="">Tüm Departmanlar</option>{departmentList.map(d => <option key={d} value={d}>{d}</option>)}</select>
                     </div>
                     <button onClick={() => { setEditingUser(null); setIsUserModalOpen(true); }} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-blue-500 shadow-lg shadow-blue-900/40"><Plus className="w-5 h-5" /> Yeni Personel</button>
                 </div>
                 {/* USING SORTED USERS */}
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">{sortedUsers.filter(u => u.fullName.toLowerCase().includes(userSearch.toLowerCase()) && (userFilterDept ? u.department === userFilterDept : true)).map(user => { const status = userStatusMap[user.id]; return ( <div key={user.id} className="bg-slate-900 rounded-2xl p-6 border border-slate-800 hover:border-slate-600 hover:shadow-2xl transition-all relative overflow-hidden"><div className={`absolute top-0 left-0 w-full h-1 ${status?.isOnLeave ? 'bg-purple-500' : status?.isInside ? 'bg-green-500' : 'bg-slate-700'}`}></div><div className="flex justify-between items-start mb-4"><div className="flex items-center gap-4"><div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-700 flex items-center justify-center text-xl font-bold text-white border border-slate-600/50">{user.fullName.charAt(0)}</div><div><h3 className="font-bold text-white text-lg leading-tight">{user.fullName}</h3><span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-400 border border-slate-700 uppercase">{user.department}</span></div></div><div className="flex gap-1"><button onClick={() => { setEditingUser(user); setIsUserModalOpen(true); }} className="p-2 hover:bg-slate-800 rounded-lg text-slate-500 hover:text-white"><Edit className="w-4 h-4"/></button><button onClick={() => setDeleteModalId(user.id)} className="p-2 hover:bg-red-500/10 rounded-lg text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4"/></button></div></div><div className="space-y-3 mb-6"><div className="flex justify-between items-center p-3 bg-slate-950/50 rounded-xl border border-slate-800/50"><span className="text-xs font-bold text-slate-500 uppercase">Durum</span>{status?.isOnLeave ? (<span className="flex items-center gap-1.5 text-purple-400 text-xs font-bold"><Plane className="w-3 h-3" /> İzinli</span>) : status?.isInside ? (<span className="flex items-center gap-1.5 text-green-400 text-xs font-bold"><div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div> İçeride</span>) : (<span className="text-slate-500 text-xs font-bold">Dışarıda</span>)}</div><div className="flex justify-between items-center px-2"><span className="text-xs text-slate-500 flex items-center gap-1">Maaş</span><span className="text-xs font-mono text-slate-300">{user.salary ? `${user.salary} ₺` : '-'}</span></div></div><div className="flex gap-2 mb-2"><button onClick={() => setReportModalUser(user)} className="flex-1 py-2 bg-blue-900/20 hover:bg-blue-600 hover:text-white text-blue-400 border border-blue-900/50 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2"><FileSpreadsheet className="w-3 h-3" /> Detay & Düzenle</button><button onClick={() => setLeavesModalUser(user)} className="py-2 px-3 bg-slate-800 hover:bg-purple-900/30 text-purple-400 text-xs font-bold rounded-lg border border-purple-900/30 transition-colors flex items-center justify-center gap-2"><Plane className="w-3 h-3" /></button></div></div> ); })}</div>
             </div>
        )}

        {/* Settings Tab Structure Remains Same */}
        {activeTab === 'settings' && config && (
            <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* ... existing settings content ... */}
                <div className="flex flex-wrap gap-2 mb-6 p-1.5 bg-slate-900 rounded-xl border border-slate-800 w-fit">
                    {[{ id: 'company', label: 'Firma & Tatil', icon: Briefcase }, { id: 'departments', label: 'Departmanlar', icon: Building }, { id: 'security', label: 'QR & Güvenlik', icon: Lock }, { id: 'data', label: 'Veritabanı & Yedek', icon: Database }].map(tab => (
                        <button key={tab.id} onClick={() => setSettingsTab(tab.id as any)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${settingsTab === tab.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}><tab.icon className="w-4 h-4" />{tab.label}</button>
                    ))}
                </div>
                <div className="space-y-6">
                    {/* Simplified for brevity as content is large, assuming no changes here based on prompt logic except keeping existing code safe */}
                    {settingsTab === 'company' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800">
                                <h2 className="text-lg font-bold text-white mb-4 border-b border-slate-800 pb-3 flex items-center gap-2"><Briefcase className="w-5 h-5 text-blue-500" /> Firma Bilgileri</h2>
                                <form onSubmit={handleSettingsSave} className="space-y-4">
                                    <div><label className="text-sm font-bold text-slate-400 mb-1 block">Firma Adı</label><input type="text" className="w-full p-3 bg-slate-950 border border-slate-700 rounded-xl text-white" value={configForm.companyName} onChange={e => setConfigForm({...configForm, companyName: e.target.value})} /></div>
                                    <div>
                                        <label className="text-sm font-bold text-slate-400 mb-1 block">Logo (URL)</label>
                                        <input type="text" placeholder="https://..." className="w-full p-3 bg-slate-950 border border-slate-700 rounded-xl text-white mb-2" value={configForm.logoUrl} onChange={e => setConfigForm({...configForm, logoUrl: e.target.value})} />
                                        <input type="file" className="text-sm text-slate-400" onChange={(e) => { const file = e.target.files?.[0]; if(file) { const reader = new FileReader(); reader.onloadend = () => { setConfigForm({...configForm, logoUrl: reader.result as string}); }; reader.readAsDataURL(file); } }} />
                                    </div>
                                    <button type="submit" className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-blue-500">Kaydet</button>
                                </form>
                            </div>
                            <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800">
                                <h2 className="text-lg font-bold text-white mb-4 border-b border-slate-800 pb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-red-500" /> Yönetici Giriş Bilgileri</h2>
                                <div className="space-y-4">
                                    <div><label className="text-sm font-bold text-slate-400 mb-1 block">Kullanıcı Adı</label><input type="text" className="w-full p-3 bg-slate-950 border border-slate-700 rounded-xl text-white" value={adminCreds.username} onChange={e => setAdminCreds({...adminCreds, username: e.target.value})} /></div>
                                    <div><label className="text-sm font-bold text-slate-400 mb-1 block">Yeni Şifre</label><input type="text" className="w-full p-3 bg-slate-950 border border-slate-700 rounded-xl text-white" placeholder="Yeni şifreyi giriniz..." value={adminCreds.password} onChange={e => setAdminCreds({...adminCreds, password: e.target.value})} /></div>
                                    <button onClick={handleUpdateAdmin} className="bg-red-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-red-500 shadow-lg shadow-red-900/20">Güncelle</button>
                                </div>
                            </div>
                            {/* Time & Holidays Sections */}
                            <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800 md:col-span-2">
                                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-emerald-500" /> Zaman Sunucusu & Saat</h2>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-slate-950 rounded-xl border border-slate-800"><div className="text-xs font-bold text-slate-500 uppercase">Cihaz Saati</div><div className="text-2xl font-mono text-slate-400">{new Date().toLocaleTimeString('tr-TR')}</div></div>
                                    <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 relative overflow-hidden"><div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/10 rounded-bl-full"></div><div className="text-xs font-bold text-emerald-500 uppercase flex items-center gap-2"><Lock className="w-3 h-3"/> Güvenli Sunucu Saati</div><div className="text-2xl font-mono text-white">{serverTime ? formatTimeTR(serverTime) : 'Yükleniyor...'}</div></div>
                                </div>
                            </div>
                            <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800 md:col-span-2">
                                <div className="flex justify-between items-center mb-4 border-b border-slate-800 pb-3"><h2 className="text-lg font-bold text-white flex items-center gap-2"><CalendarDays className="w-5 h-5 text-purple-500" /> Resmi Tatiller</h2><button onClick={autoAddHolidays} className="text-xs font-bold text-purple-400 bg-purple-500/10 px-3 py-1.5 rounded-lg border border-purple-500/30">Otomatik Getir</button></div>
                                <div className="flex gap-2 mb-4"><input type="date" className="flex-1 p-2.5 bg-slate-950 border border-slate-700 rounded-xl text-white" value={newHoliday} onChange={e => setNewHoliday(e.target.value)} /><button onClick={addHoliday} className="px-5 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-500">Ekle</button></div>
                                <div className="flex flex-wrap gap-2">{configForm.publicHolidays?.map(date => (<span key={date} className="bg-slate-800 text-slate-300 px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 border border-slate-700 group hover:border-red-500/50 transition-colors">{new Date(date).toLocaleDateString('tr-TR')}<button onClick={() => removeHoliday(date)} className="text-slate-500 group-hover:text-red-500"><X className="w-3 h-3" /></button></span>))}</div>
                                <button onClick={handleSettingsSave} className="mt-6 bg-slate-800 text-slate-200 px-6 py-2.5 rounded-xl font-bold hover:bg-slate-700 border border-slate-700">Güncelle</button>
                            </div>
                        </div>
                    )}
                    {settingsTab === 'departments' && (
                        <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800">
                            <h2 className="text-lg font-bold text-white mb-6">Departman Yönetimi</h2>
                            <div className="p-4 bg-slate-950 rounded-xl border border-slate-800">
                                <div className="flex gap-2 mb-4"><input type="text" placeholder="Yeni Departman Adı..." className="flex-1 p-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white" value={newDeptName} onChange={e => setNewDeptName(e.target.value)} /><button onClick={handleAddDepartment} className="bg-green-600 text-white px-5 rounded-lg font-bold hover:bg-green-500">Ekle</button></div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{configForm.departments?.map(dept => (<div key={dept} className="bg-slate-900 p-3 rounded-lg border border-slate-700 flex justify-between items-center group hover:border-slate-600"><span className="text-white font-medium">{dept}</span><button onClick={() => removeDepartment(dept)} className="text-slate-500 hover:text-red-500 hover:bg-red-500/10 p-1.5 rounded transition-colors"><Trash2 className="w-4 h-4" /></button></div>))}</div>
                            </div>
                            <button onClick={handleSettingsSave} className="mt-6 bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-blue-500">Kaydet</button>
                        </div>
                    )}
                    {settingsTab === 'security' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-slate-900 p-8 rounded-2xl shadow-lg border border-slate-800 text-center flex flex-col items-center">
                                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2"><Printer className="w-6 h-6 text-slate-400"/> Sabit QR Kod</h2>
                                <div className="flex-1 flex items-center justify-center bg-white p-4 rounded-xl border-4 border-slate-700 w-fit mb-6">
                                     {config.qrSecret ? (
                                        // Use Level "M" instead of "H" for better readability
                                        <QRCodeSVG value={config.qrSecret} size={180} level={"M"} includeMargin={true}/>
                                     ) : (
                                        <div className="w-[180px] h-[180px] flex items-center justify-center text-black font-bold">Yükleniyor...</div>
                                     )}
                                </div>
                                <div className="flex gap-2 w-full"><button onClick={() => window.print()} className="flex-1 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 font-bold border border-slate-700 flex items-center justify-center gap-2"><Printer className="w-4 h-4"/> Yazdır</button><button onClick={regenerateStaticQR} className="flex-1 py-3 bg-red-900/20 text-red-400 rounded-xl hover:bg-red-900/30 font-bold border border-red-900/50 flex items-center justify-center gap-2"><RefreshCw className="w-4 h-4"/> Değiştir</button></div>
                            </div>
                            <div className="bg-slate-900 p-8 rounded-2xl shadow-lg border border-slate-800 flex flex-col">
                                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Lock className="w-5 h-5 text-yellow-500"/> Kiosk Terminal Hesabı</h2>
                                <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 mb-6"><div className="text-xs font-bold text-slate-500 uppercase mb-1">Kullanıcı Adı</div><div className="text-white font-mono mb-4">qr@technosis.com.tr</div><div className="text-xs font-bold text-slate-500 uppercase mb-1">Yeni Şifre</div><div className="flex gap-2"><input type="text" className="w-full p-2 bg-slate-900 border border-slate-700 rounded-lg text-white" placeholder="Yeni şifre belirle..." value={kioskPassword} onChange={e => setKioskPassword(e.target.value)} /><button onClick={handleUpdateKioskUser} className="bg-yellow-600 text-white px-4 rounded-lg font-bold hover:bg-yellow-500">Güncelle</button></div></div>
                                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2 mt-auto"><RefreshCw className="w-5 h-5 text-blue-500"/> Dinamik QR Ayarları</h2>
                                <div className="w-full bg-slate-950 p-4 rounded-xl border border-slate-800 mb-4"><label className="block text-xs font-bold text-slate-500 mb-2 uppercase">Yenileme Süresi (Saniye)</label><div className="flex gap-2"><input type="number" className="w-full p-2 bg-slate-900 border border-slate-700 rounded-lg text-white font-mono text-center font-bold" value={configForm.qrRefreshSeconds} onChange={e => setConfigForm({...configForm, qrRefreshSeconds: Number(e.target.value)})} /><button onClick={handleSettingsSave} className="bg-blue-600 text-white px-4 rounded-lg font-bold hover:bg-blue-500">Kaydet</button></div></div>
                                <a href="/#kiosk" target="_blank" className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-blue-900/40 transition-all flex items-center justify-center gap-2"><ExternalLink className="w-4 h-4"/> Kiosk Ekranını Aç</a>
                            </div>
                        </div>
                    )}
                    {settingsTab === 'data' && (
                        <div className="space-y-6">
                            <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-bl-[100px]"></div>
                                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2 relative z-10"><Cloud className="w-5 h-5 text-blue-500"/> Bulut Yedekleme</h2>
                                <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-xl mb-6 flex items-center gap-3"><Clock className="w-5 h-5 text-blue-400" /><div className="text-sm text-blue-200"><span className="font-bold">Otomatik Yedekleme:</span> Sistem her gün saat <span className="text-white font-bold">18:00</span>'de otomatik yedek alır.</div></div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase">Yedek Geçmişi</h3>
                                    <button onClick={handleCreateCloudBackup} disabled={isCloudLoading} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold text-sm flex items-center gap-2">
                                        {isCloudLoading ? <Loader2 className="animate-spin w-4 h-4"/> : <Plus className="w-4 h-4"/>} Manuel Yedek Al
                                    </button>
                                </div>
                                
                                {isCloudLoading && cloudStatusMessage && (
                                    <div className="mb-4 p-3 bg-blue-900/30 border border-blue-500/30 rounded-lg text-blue-200 text-sm font-bold flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin"/> {cloudStatusMessage}
                                    </div>
                                )}

                                <div className="max-h-60 overflow-y-auto pr-2 space-y-2">{cloudBackups.length === 0 && <div className="text-center text-slate-500 py-4 text-sm">Henüz yedek alınmamış.</div>}{cloudBackups.map(backup => (<div key={backup.id} className="p-3 bg-slate-950 rounded-lg border border-slate-800 flex justify-between items-center hover:border-slate-600 transition-colors"><div><div className="flex items-center gap-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${backup.creator === 'AUTO' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>{backup.creator}</span><span className="text-white font-mono text-sm">{new Date(backup.timestamp).toLocaleString('tr-TR')}</span></div><div className="text-xs text-slate-500 mt-1">Boyut: {backup.size} • {backup.totalChunks ? `${backup.totalChunks} Parça` : 'Tek Parça'} • Durum: {backup.status || 'OK'}</div></div><div className="flex gap-2"><button onClick={() => handleRestoreCloud(backup.id)} disabled={isCloudLoading} className="p-2 hover:bg-green-500/10 text-slate-400 hover:text-green-500 rounded"><RefreshCw className="w-4 h-4"/></button><button onClick={() => handleDeleteCloud(backup.id)} disabled={isCloudLoading} className="p-2 hover:bg-red-500/10 text-slate-400 hover:text-red-500 rounded"><Trash2 className="w-4 h-4"/></button></div></div>))}</div>
                            </div>
                            <div className="bg-slate-900 p-6 rounded-2xl shadow-lg border border-slate-800">
                                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Database className="w-5 h-5 text-emerald-500"/> Yerel Dosya İşlemleri</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><button onClick={handleDownloadBackup} disabled={isBackingUp} className="py-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-white font-bold flex flex-col items-center justify-center gap-2 transition-all hover:border-emerald-500/50 group">{isBackingUp ? <Loader2 className="animate-spin text-emerald-500"/> : <Download className="group-hover:text-emerald-500 transition-colors"/>} <span>Yedek Dosyası İndir</span></button><div className="relative"><input type="file" ref={fileInputRef} onChange={handleRestoreBackup} className="hidden" id="restore" /><label htmlFor="restore" className="py-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-white font-bold flex flex-col items-center justify-center gap-2 cursor-pointer transition-all hover:border-blue-500/50 group h-full">{isRestoring ? <Loader2 className="animate-spin text-blue-500"/> : <Database className="group-hover:text-blue-500 transition-colors"/>} <span>Yedek Dosyası Yükle</span></label></div></div>
                                <div className="mt-6 pt-6 border-t border-slate-800"><button onClick={handleDatabaseRepair} disabled={isRepairing} className="w-full py-3 bg-yellow-900/10 hover:bg-yellow-900/20 text-yellow-500 border border-yellow-500/30 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors">{isRepairing ? <Loader2 className="animate-spin"/> : <Wrench className="w-4 h-4"/>} Veritabanı Onarım & Bakım</button></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )}
      </main>
    </div>
  );
};
